import React, { useState, useEffect, useCallback } from 'react';
import { TimeEntry } from '../../types/worksheet';
import { timeTrackingService } from '../../lib/services/timeTrackingService';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlayIcon,
  StopIcon,
  ClockIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';

interface TimeTrackerProps {
  worksheetId: string;
  entries: TimeEntry[];
  onEntriesChange: () => void;
}

const TimeTracker: React.FC<TimeTrackerProps> = ({ worksheetId, entries, onEntriesChange }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [activeTimerId, setActiveTimerId] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [manualWorkers, setManualWorkers] = useState(1);

  // Check for active timer
  useEffect(() => {
    const active = entries.find(e => !e.endTime);
    if (active) {
      setActiveTimerId(active.id);
      const start = active.startTime instanceof Date ? active.startTime : new Date(active.startTime as any);
      setElapsed(Math.floor((Date.now() - start.getTime()) / 60000));
    } else {
      setActiveTimerId(null);
      setElapsed(0);
    }
  }, [entries]);

  // Update elapsed time every minute
  useEffect(() => {
    if (!activeTimerId) return;
    const interval = setInterval(() => {
      const active = entries.find(e => e.id === activeTimerId);
      if (active) {
        const start = active.startTime instanceof Date ? active.startTime : new Date(active.startTime as any);
        setElapsed(Math.floor((Date.now() - start.getTime()) / 60000));
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTimerId, entries]);

  const handleStart = useCallback(async () => {
    await timeTrackingService.startTimer(worksheetId, user?.uid || '', user?.displayName || '');
    onEntriesChange();
  }, [worksheetId, user, onEntriesChange]);

  const handleStop = useCallback(async () => {
    if (activeTimerId) {
      await timeTrackingService.stopTimer(activeTimerId);
      setActiveTimerId(null);
      onEntriesChange();
    }
  }, [activeTimerId, onEntriesChange]);

  const handleDelete = useCallback(async (entryId: string) => {
    await timeTrackingService.deleteEntry(entryId);
    onEntriesChange();
  }, [onEntriesChange]);

  const handleAddManual = useCallback(async () => {
    if (!manualStart || !manualEnd) return;
    const start = new Date(manualStart);
    const end = new Date(manualEnd);
    if (end <= start) return;
    await timeTrackingService.addManualEntry(
      worksheetId,
      user?.uid || '',
      user?.displayName || '',
      start,
      end,
      manualDesc,
      manualWorkers
    );
    setManualStart('');
    setManualEnd('');
    setManualDesc('');
    setManualWorkers(1);
    setShowManualForm(false);
    onEntriesChange();
  }, [worksheetId, user, manualStart, manualEnd, manualDesc, manualWorkers, onEntriesChange]);

  const formatDuration = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}ó ${m}p`;
  };

  const totalMinutes = entries.reduce((sum, e) => sum + (e.duration || 0) * (e.workerCount || 1), 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-y-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700">
        <div className="flex items-center space-x-2">
          <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          <h3 className="text-xs sm:text-sm font-semibold text-white">{t('worksheets.timeTracking.title')}</h3>
          <span className="text-[10px] sm:text-xs text-white/80 whitespace-nowrap">({formatDuration(totalMinutes)})</span>
        </div>
        <div className="flex items-center space-x-1.5 sm:space-x-2 whitespace-nowrap">
          {activeTimerId ? (
            <button
              onClick={handleStop}
              className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
            >
              <StopIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              {t('worksheets.timeTracking.stop')} ({formatDuration(elapsed)})
            </button>
          ) : (
            <button
              onClick={handleStart}
              className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
            >
              <PlayIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              {t('worksheets.timeTracking.start')}
            </button>
          )}
          <button
            onClick={() => setShowManualForm(!showManualForm)}
            className="inline-flex items-center px-2 py-1 sm:px-2 sm:py-1.5 text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <PlusIcon className="w-3 h-3 mr-1" />
            {t('worksheets.timeTracking.manual')}
          </button>
        </div>
      </div>

      {/* Manual entry form */}
      {showManualForm && (
        <div className="px-3 py-2 sm:px-4 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] sm:text-xs text-gray-500">{t('worksheets.timeTracking.startLabel')}</label>
              <input
                type="datetime-local"
                value={manualStart}
                onChange={e => setManualStart(e.target.value)}
                className="crm-input-raw crm-input-compact w-full text-sm border border-[var(--crm-border)] rounded-md px-2 bg-[var(--crm-surface)] text-gray-700 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">{t('worksheets.timeTracking.endLabel')}</label>
              <input
                type="datetime-local"
                value={manualEnd}
                onChange={e => setManualEnd(e.target.value)}
                className="crm-input-raw crm-input-compact w-full text-sm border border-[var(--crm-border)] rounded-md px-2 bg-[var(--crm-surface)] text-gray-700 dark:text-gray-200"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={manualDesc}
              onChange={e => setManualDesc(e.target.value)}
              placeholder={t('worksheets.timeTracking.description')}
              className="crm-input-raw crm-input-compact text-sm border border-[var(--crm-border)] rounded-md px-2 bg-[var(--crm-surface)] text-gray-700 dark:text-gray-200"
            />
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={manualWorkers}
                onChange={e => setManualWorkers(parseInt(e.target.value) || 1)}
                min={1}
                className="crm-input-raw crm-input-compact w-16 text-sm border border-[var(--crm-border)] rounded-md px-2 bg-[var(--crm-surface)] text-gray-700 dark:text-gray-200"
              />
              <span className="text-[10px] sm:text-xs text-gray-500">{t('worksheets.timeTracking.workers')}</span>
              <button
                onClick={handleAddManual}
                className="ml-auto px-3 py-1 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
              >
                {t('worksheets.timeTracking.add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Entries list */}
      {entries.length > 0 ? (
        <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
          {entries.map((entry) => {
            const start = entry.startTime instanceof Date ? entry.startTime : new Date(entry.startTime as any);
            const end = entry.endTime instanceof Date ? entry.endTime : entry.endTime ? new Date(entry.endTime as any) : null;
            return (
              <div key={entry.id} className="px-3 py-1.5 sm:px-4 sm:py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className={`w-2 h-2 rounded-full ${!end ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  <div>
                    <p className="text-[10px] sm:text-xs font-medium text-gray-700 dark:text-gray-300">
                      {format(start, 'MM.dd HH:mm', { locale: hu })}
                      {end && ` → ${format(end, 'HH:mm', { locale: hu })}`}
                    </p>
                    {entry.description && (
                      <p className="text-[10px] sm:text-xs text-gray-500">{entry.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {entry.workerCount && entry.workerCount > 1 && (
                    <span className="text-[10px] sm:text-xs text-gray-400">{entry.workerCount}{t('worksheets.timeTracking.personTimes')}</span>
                  )}
                  {entry.isManual && (
                    <span className="text-[10px] sm:text-xs text-blue-400">{t('worksheets.timeTracking.manualType')}</span>
                  )}
                  <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    {formatDuration(entry.duration || 0)}
                  </span>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="p-1 text-red-400 hover:text-red-600"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-3 py-4 sm:px-4 sm:py-6 text-center text-xs sm:text-sm text-gray-400">
          {t('worksheets.timeTracking.noEntries')}
        </div>
      )}
    </div>
  );
};

export default TimeTracker;
